import { FetchApi } from '@backstage/core-plugin-api';
import { OpenIdConnectApi } from '@backstage/core-plugin-api';
import { Page, PageRequest, ScheduleApiError } from './types';

export interface SchedulerProjection {
  id: string;
  workspace: string;
  requester: string;
  state: string;
  details: string;
  initialPeriod: string;
  finalPeriod: string;
  recurrency: string;
  executions: number;
  averageExecutionTime: number;
  executionLimit: number;
  taskCode: string;
  taskDescription: string;
  taskData: any;
  nextExecution: string;
}

export interface ExecutionsProjection {
  id: string;
  state: string;
  logs: string;
  errorMessage: string;
  requestStartTime: string;
  requestEndTime: string;  
  executionStartTime: string;
  executionFinishTime: string;
}

export interface MaintenanceTask {
  taskCode: string;
  taskData?: any;
  recurrency: string;
  workspace: string;
  requester: string;
  initialPeriod: string;
  finalPeriod: string;
  executionLimit: number;
}

export const ALL_TENANTS = 'all-tenants';
export class ScheduleService {
    constructor(
      private readonly apiUrl: string,
      private readonly fetchApi: FetchApi,
      private readonly env: string,
      private readonly authApi: OpenIdConnectApi
    ) {}

    private async getAuthHeaders(): Promise<Headers> {
      const token = await this.authApi.getIdToken();
      const headers = new Headers();
      headers.set('Authorization', `Bearer ${token}`);
      return headers;
    }
  
    async listSchedules(taskCode: string | undefined, workspace: string, requester: string, page?: PageRequest): Promise<Page<SchedulerProjection>> {
      const url = new URL(`${this.apiUrl}/open-scheduler-${this.env}/api/v1/schedules`);
      
      if (workspace !== ALL_TENANTS){
        url.searchParams.append('workspace', workspace);
      }
      
      url.searchParams.append('requester', requester);
      if(taskCode)
        url.searchParams.append('taskCode', taskCode);

      if (page && page.page !== undefined)
        url.searchParams.append('page', page.page.toString());
      if (page && page.size !== undefined)
        url.searchParams.append('size', page.size.toString());
      if (page && page.sort && page.sortDirection)
        url.searchParams.append('sort', `${page.sort},${page.sortDirection}`);

      const headers = await this.getAuthHeaders();

      return this.fetchApi.fetch(url.toString(), { headers })
        .then(response => {
          if (!response.ok) {
            throw new Error(`Failed to fetch schedules: ${response.statusText}`);
          }
          return response.json();
        });
    }
  
    async listExecutions(scheduleId: string): Promise<ExecutionsProjection[]> {
      const url = `${this.apiUrl}/open-scheduler-${this.env}/api/v1/schedules/${scheduleId}/executions`;

      const headers = await this.getAuthHeaders();

      return this.fetchApi.fetch(url, { headers })
        .then(response => {
          if (!response.ok) {
            throw new Error(`Failed to fetch executions: ${response.statusText}`);
          }
          return response.json();
        });
    }
  
    async enableSchedule(scheduleId: string, reason: string): Promise<void> {
      const url = `${this.apiUrl}/open-scheduler-${this.env}/api/v1/schedules/${scheduleId}/enable`;

      const headers = await this.getAuthHeaders();

      return this.fetchApi.fetch(url, { headers, method: 'PATCH', body: JSON.stringify({ reason }) })
        .then(response => {
          if (!response.ok) {
            throw new Error(`Failed to fetch executions: ${response.statusText}`);
          }
          return;
        });
    }
  
    async disableSchedule(scheduleId: string, reason: string): Promise<void> {
      const url = `${this.apiUrl}/open-scheduler-${this.env}/api/v1/schedules/${scheduleId}/disable`;

      const headers = await this.getAuthHeaders();

      return this.fetchApi.fetch(url, { headers, method: 'PATCH', body: JSON.stringify({ reason }) })
        .then(response => {
          if (!response.ok) {
            throw new Error(`Failed to fetch executions: ${response.statusText}`);
          }
          return;
        });
    }

    async runMaintenanceTask(task: MaintenanceTask): Promise<void> {
      const url = `${this.apiUrl}/open-scheduler-${this.env}/api/v1/schedules/maintenance-task`;

      const headers = await this.getAuthHeaders();

      return this.fetchApi.fetch(url, { headers, method: 'POST', body: JSON.stringify(task) })
        .then(async response => {
          if (!response.ok) {
            const error: ScheduleApiError = await response.json();
            throw new Error(error.title);
          }
          return;
        });
    }
}